// First Created - 12 Jun 2026
// Author - Atul Pandey
// Purpose - To delete / deactivate an Org Trail Session Cohort / Batch for Trinity trail delivery

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import Modal from "@/components/common/modal/Modal"

import {
  useGetOrgTrailSessionCohortsQuery,
  useDeleteOrgTrailSessionCohortMutation,
} from "./orgTrailSessionCohortsApiSlice"

const EtDeleteOrgTrailSessionCohort = ({ id, setActiveAdminView }) => {
  // First Created - 12 Jun 2026
  // Author - Atul Pandey
  // Purpose - To delete / deactivate an Org Trail Session Cohort / Batch for Trinity trail delivery
  // Updated - 14 Jun 2026 - added cohortUserIds

  useTitle("Delete Cohort")

  const { orgTrailSessionCohort } = useGetOrgTrailSessionCohortsQuery(
    { active: true },
    {
      selectFromResult: ({ data }) => ({
        orgTrailSessionCohort: data?.entities[id],
      }),
    }
  )

  if (!orgTrailSessionCohort) {
    return <PulseLoader color={"#ea580c"} />
  }

  return (
    <EtDeleteOrgTrailSessionCohortForm
      orgTrailSessionCohort={orgTrailSessionCohort}
      id={id}
      setActiveAdminView={setActiveAdminView}
    />
  )
}

const EtDeleteOrgTrailSessionCohortForm = ({
  orgTrailSessionCohort,
  id,
  setActiveAdminView,
}) => {
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState("soft")

  const [
    deleteOrgTrailSessionCohort,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    },
  ] = useDeleteOrgTrailSessionCohortMutation()

  const formatDate = value => {
    if (!value) return "Not defined"

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) return "Not defined"

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const [formData, setFormData] = useState({
    cohortName: orgTrailSessionCohort.cohortName || "",
    cohortCode: orgTrailSessionCohort.cohortCode || "",
    cohortDesc: orgTrailSessionCohort.cohortDesc || "",
    trailName: orgTrailSessionCohort.trailId?.trailName || "",
    cohortStatus: orgTrailSessionCohort.cohortStatus || "Planned",
    plannedStartDate: orgTrailSessionCohort.plannedStartDate || "",
    plannedEndDate: orgTrailSessionCohort.plannedEndDate || "",
    coachCount: orgTrailSessionCohort.coachIds?.length || 0,
    sponsorCount: orgTrailSessionCohort.sponsorIds?.length || 0,
    cohortUserCount: orgTrailSessionCohort.cohortUserIds?.length || 0, // 14 Jun 2026
    active: orgTrailSessionCohort.active ?? true,
  })

  useEffect(() => {
    if (isSuccess) {
      setFormData({
        cohortName: "",
        cohortCode: "",
        cohortDesc: "",
        trailName: "",
        cohortStatus: "Planned",
        plannedStartDate: "",
        plannedEndDate: "",
        coachCount: 0,
        sponsorCount: 0,
        cohortUserCount: 0, // 14 Jun 2026
        active: true,
      })

      if (setActiveAdminView) {
        setActiveAdminView("orgTrailSessionCohorts")
      } else {
        navigate("/orgTrailSessionCohorts")
      }
    }
  }, [isSuccess])

  const handleOpenModal = (mode = "soft") => {
    setDeleteMode(mode)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => setIsModalOpen(false)

  const handleConfirm = async e => {
    e.preventDefault()

    try {
      await deleteOrgTrailSessionCohort({ id, deleteMode }).unwrap()

      if (setActiveAdminView) {
        setActiveAdminView("orgTrailSessionCohorts")
      } else {
        navigate("/orgTrailSessionCohorts")
      }
    } catch (error) {
      console.error(error)
      alert(error?.data?.message || "Failed to delete cohort") // 14 Jun 2026
    } finally {
      handleCloseModal()
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("orgTrailSessionCohorts")
    } else {
      navigate("/orgTrailSessionCohorts")
    }
  }

  return (
    <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Delete Cohort
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form
            onSubmit={e => {
              e.preventDefault()
              handleOpenModal("soft")
            }}
            className="m-1 p-1 text-xs font-poppins"
          >
            <input
              type="text"
              name="cohortName"
              placeholder="Cohort name"
              value={formData.cohortName}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <input
              type="text"
              name="cohortCode"
              placeholder="Cohort code"
              value={formData.cohortCode}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <input
              type="text"
              name="cohortDesc"
              placeholder="Cohort description"
              value={formData.cohortDesc}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <input
              type="text"
              name="trailName"
              placeholder="Trail"
              value={`Trail: ${formData.trailName || "Not defined"}`}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <div className="grid grid-cols-4 gap-1">
                <input
                    type="text"
                    name="cohortStatus"
                    placeholder="Cohort status"
                    value={`Status: ${formData.cohortStatus}`}
                    disabled
                    className="w-full rounded p-2 m-1 bg-gray-100"
                />

                <input
                    type="text"
                    name="coachCount"
                    placeholder="Coach count"
                    value={`Coaches: ${formData.coachCount}`}
                    disabled
                    className="w-full rounded p-2 m-1 bg-gray-100"
                />

                <input
                    type="text"
                    name="sponsorCount"
                    placeholder="Sponsor count"
                    value={`Sponsors: ${formData.sponsorCount}`}
                    disabled
                    className="w-full rounded p-2 m-1 bg-gray-100"
                />

                <input
                    type="text"
                    name="cohortUserCount"
                    placeholder="Cohort user count"
                    value={`Users: ${formData.cohortUserCount}`}
                    disabled
                    className="w-full rounded p-2 m-1 bg-gray-100"
                />
            </div>

            <div className="grid grid-cols-3 gap-1">
              <input
                type="text"
                name="plannedStartDate"
                placeholder="Planned start date"
                value={`Start: ${formatDate(formData.plannedStartDate)}`}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="text"
                name="plannedEndDate"
                placeholder="Planned end date"
                value={`End: ${formatDate(formData.plannedEndDate)}`}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="text"
                name="active"
                placeholder="Active status"
                value={formData.active ? "Active" : "Inactive"}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded m-1 p-2">
                <p className="text-xs text-orange-700">
                    You can deactivate this cohort or permanently delete it only if it has not
                    been used for user trail session plans or executions. If this cohort is
                    already assigned to users, the backend will block both deactivate and hard delete.
                </p>
              {/* <p className="text-xs text-orange-700">
                You can either deactivate this cohort or permanently delete it.
                Deactivation keeps the cohort in history. Hard delete removes it
                permanently. If this cohort already has user plans or executions,
                the backend will block hard delete and ask you to deactivate it instead.
              </p> */}
            </div>

            <div id="cohortActions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Make Inactive"}
                type={"clear"}
                handleClick={() => handleOpenModal("soft")}
              />

              <EtBtnFormBasic
                name={"Hard Delete"}
                type={"create"}
                handleClick={() => handleOpenModal("hard")}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

export default EtDeleteOrgTrailSessionCohort